/**
 * PlanSelector Component
 * Grid of plan cards with interval toggle and coupon input
 */
import { useState, useMemo, useCallback } from 'react';
import PlanCard from './PlanCard';
import CouponInput from './CouponInput';
import { cn } from '@/utils/cn';
import type { Plan, PlanInterval, CouponValidation } from '@/types/billing';

export interface PlanSelectorProps {
  plans: Plan[];
  currentPlanId?: string | null;
  onSelectPlan: (plan: Plan, couponCode?: string) => void;
  loadingPlanId?: string | null;
  /** WP-BILLING-19: false for a plan change (coupons apply to purchases only). */
  showCoupon?: boolean;
  className?: string;
}

/**
 * Calculate discounted price based on coupon
 * WP-BILLING-11: Handle both legacy names (percent/fixed) and backend names (percent_off/amount_off)
 * WP-BILLING-13: Handle FREE_MONTHS coupons (free during trial period)
 */
function calculateDiscountedPrice(
  amount: number,
  coupon: CouponValidation | null
): number | null {
  if (!coupon || !coupon.valid) {
    return null;
  }

  // WP-BILLING-11: FREE_FOREVER coupons make the plan free
  if (coupon.discountType === 'free_forever') {
    return 0;
  }

  // WP-BILLING-13: FREE_MONTHS coupons - show as free during trial period
  if (coupon.discountType === 'free_months') {
    return 0;
  }

  if (!coupon.discountValue) {
    return null;
  }

  // Handle both 'percent' (legacy) and 'percent_off' (backend) discount types
  if (coupon.discountType === 'percent' || coupon.discountType === 'percent_off') {
    const discount = amount * (coupon.discountValue / 100);
    return Math.round(amount - discount);
  }

  // Handle both 'fixed' (legacy) and 'amount_off' (backend) discount types
  if (coupon.discountType === 'fixed' || coupon.discountType === 'amount_off') {
    return Math.max(0, amount - coupon.discountValue);
  }

  return null;
}

export default function PlanSelector({
  plans,
  currentPlanId,
  onSelectPlan,
  loadingPlanId,
  showCoupon = true,
  className,
}: PlanSelectorProps) {
  const [selectedInterval, setSelectedInterval] = useState<PlanInterval>('monthly');
  const [appliedCoupon, setAppliedCoupon] = useState<CouponValidation | null>(null);

  // Filter plans by selected interval
  const filteredPlans = useMemo(() => {
    return plans
      .filter((plan) => plan.interval === selectedInterval)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [plans, selectedInterval]);

  // Check if yearly plans are available
  const hasYearlyPlans = useMemo(() => {
    return plans.some((plan) => plan.interval === 'yearly');
  }, [plans]);

  // Calculate actual savings percentage for yearly vs monthly
  const yearlySavingsPercent = useMemo(() => {
    const monthlyPlan = plans.find((p) => p.interval === 'monthly');
    const yearlyPlan = plans.find((p) => p.interval === 'yearly');
    if (!monthlyPlan || !yearlyPlan) return 0;
    const yearlyIfMonthly = monthlyPlan.amount * 12;
    const savings = yearlyIfMonthly - yearlyPlan.amount;
    return Math.round((savings / yearlyIfMonthly) * 100);
  }, [plans]);

  const handleSelectPlan = useCallback(
    (plan: Plan) => {
      onSelectPlan(plan, appliedCoupon?.valid ? appliedCoupon.code : undefined);
    },
    [onSelectPlan, appliedCoupon]
  );

  const handleCouponValidated = useCallback((coupon: CouponValidation | null) => {
    setAppliedCoupon(coupon);
  }, []);

  return (
    <div className={cn('space-y-6', className)}>
      {/* Interval toggle - only show if yearly plans exist */}
      {hasYearlyPlans && (
        <div className="flex justify-center">
          <div className="inline-flex items-center p-1 bg-gray-100 rounded-lg" role="radiogroup" aria-label="Billing interval">
            <button
              type="button"
              role="radio"
              aria-checked={selectedInterval === 'monthly'}
              onClick={() => setSelectedInterval('monthly')}
              className={cn(
                'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                selectedInterval === 'monthly'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              )}
            >
              Monthly
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={selectedInterval === 'yearly'}
              onClick={() => setSelectedInterval('yearly')}
              className={cn(
                'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                selectedInterval === 'yearly'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              )}
            >
              Yearly
              {yearlySavingsPercent > 0 && (
                <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                  Save {yearlySavingsPercent}%
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Coupon input */}
      {showCoupon && (
        <div className="max-w-md mx-auto">
          <CouponInput onCouponValidated={handleCouponValidated} />
        </div>
      )}

      {/* Plan cards grid */}
      <div
        className={cn(
          'grid gap-6',
          filteredPlans.length === 1 && 'max-w-md mx-auto',
          filteredPlans.length === 2 && 'grid-cols-1 md:grid-cols-2 max-w-2xl mx-auto',
          filteredPlans.length === 3 && 'grid-cols-1 md:grid-cols-3',
          filteredPlans.length >= 4 && 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
        )}
      >
        {filteredPlans.map((plan) => {
          const discountedAmount = calculateDiscountedPrice(plan.amount, appliedCoupon);
          // WP-BILLING-11: Pass FREE_FOREVER status to PlanCard for button text
          const isFreeForeverCoupon = appliedCoupon?.valid && appliedCoupon.discountType === 'free_forever';
          // WP-BILLING-13: Pass FREE_MONTHS status to PlanCard
          const isFreeMonthsCoupon = appliedCoupon?.valid && appliedCoupon.discountType === 'free_months';

          return (
            <PlanCard
              key={plan.id}
              plan={plan}
              isCurrentPlan={plan.id === currentPlanId}
              isLoading={loadingPlanId === plan.id}
              discountedAmount={discountedAmount}
              isFreeForever={isFreeForeverCoupon}
              isFreeMonths={isFreeMonthsCoupon}
              freeMonthsDisplay={appliedCoupon?.discountDisplay}
              onSelect={() => handleSelectPlan(plan)}
            />
          );
        })}
      </div>

      {/* Empty state */}
      {filteredPlans.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500">
            No plans available for {selectedInterval} billing.
          </p>
        </div>
      )}
    </div>
  );
}
